import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';

import {map} from 'rxjs/operators';

import {
    AccountCloseoutAlgorithm,
    AccountSubTypeEnum,
    CashAccountDetailedReportModel,
    CompanyExtendedModel,
    CurrencyModel,
    CurrencyTypesEnum,
    FundPortalCashAccountInputModel,
    FundPortalService,
    FundPortalTradingAccountInputModel,
    HttpStatusCodesEnum,
    IndividualExtendedModel,
    OmsRiskAction,
    OmsRiskParametersModel,
    OrderRouteService,
    PortalsEnum,
    RouteConfigInputModel,
    RouteSearchOutputItemModel,
    RouteSearchOutputModel,
    RouteSetSearchOutputModel,
    ServerResponse,
    ServerResponseData,
    TradingAccountDetailedReportModel,
    TradingAccountInputModel,
    TradingAccountInterface,
    TradingAccountModel,
    TradingAccountPutModel,
    TradingAccountStatusesEnum,
    TradingGroupModel,
    TradingGroupStatusesEnum,
    TradingService,
    UserBehaviourTypesEnum,
    OmsRiskActionModel,
    TotalDynExpMultModel,
    LimitsModel,
    LossLimitPctModel,
    MaxOrderQtyModel,
    PosLimitPctModel,
    ShortSaleModel,
    BatchCreateAccountInputModel,
    BatchCreateAccountResultInterface
} from '@spa/api';

import {AmountMaxValidatorKey, AmountMinValidatorKey, InArrayValidator} from '@spa/validators';

import {CheckboxControl, Helper, SelectControl, TextsHelper} from 'appShared/classes';
import {SelectOptionInterface} from 'appShared/components';

import {
    AlaricFundPortalAccountIdentifier,
    DemoAlaricProPortalAccountIdentifier,
    HammerWebPortalAccountIdentifier,
    TenEnPortalAccountIdentifier,
} from 'appShared/constants';

import {ClientTypesEnum, TradingAccountsFormErrorStatesEnum} from '../enums';

import {
    EditAccountClearingAgentSelector,
    SelectorActionDoNothing,
    SelectorActionPopupWarning,
    SelectorActionRejectOrder,
    SelectorActionSendEmail,
    SelectorActionSendSms,
    SelectorActionSetToBlock,
    SelectorActionSetToCloseout,
    TradingAccountsAccountSubType,
    TradingAccountsCashControlsInterface,
    TradingAccountsCashCurrencySelector,
    TradingAccountsCashGroupSelector,
    TradingAccountsDisabledSelector,
    TradingAccountsExecutionRoutesSelector,
    TradingAccountsFirmIdSelector,
    TradingAccountsGroupIdSelector,
    TradingAccountsLossLimitActionSelector,
    TradingAccountsLossLimitSelector,
    TradingAccountsMaxOrderQtyActionSelector,
    TradingAccountsMaxOrderQtySelector,
    TradingAccountsPosLimitActionSelector,
    TradingAccountsPosLimitSelector,
    TradingAccountsNumberOfAccountsSelector,
    TradingAccountsShortSaleActionSelector, TradingAccountsSkipFundingSelector,
    TradingAccountsTotalDynExpActionSelector,
    TradingAccountsTotalDynExpLimitSelector,
    TradingAccountsTotalDynExpMultSelector,
    TradingAccountsTradingAlias,
    TradingAccountsTradingClearingAccountRefIdSelector, TradingAccountsTradingClearingAgentSelector,
    TradingAccountsTradingControlsInterface,
    TradingAccountsTradingCurrencySelector, TradingAccountsTradingPlatformSelector,
} from 'appAssets/descriptors';

import {PanelAccountsAddCashMessages, PanelAccountsAddTradingMessages} from 'appAssets/messages';
import {ColorCodesEnum, ComponentTypesEnum} from 'appShared/enums';
import {AlertsService} from "appShared/services";

type groupedTradingGroupsOptions = {
    options: SelectOptionInterface[],
    ids: number[]
};

export class TradingAccountsForm {
    private readonly tradingService: TradingService;
    private readonly fundPortalService: FundPortalService;
    private readonly tradingGroups: TradingGroupModel[];
    private readonly currencies: CurrencyModel[];
    private readonly relatedCashAccount: TradingAccountModel;
    private readonly account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel;
    private accountId: number;
    private accountTradingPlatformId: number;
    private skipFundingOperatorTouched = false;
    private suppressSkipFundingTouched = false;
    private omsSkipFundingDefault = false;
    public routeFormControlsRouteArraySelector: string;

    public form: FormGroup;
    public formCreationErrors: string[];
    public controls: TradingAccountsCashControlsInterface | TradingAccountsTradingControlsInterface;
    public availableRoutes: RouteSetSearchOutputModel;

    constructor(
        private alertsService: AlertsService,
        tradingService: TradingService,
        controls: TradingAccountsCashControlsInterface | TradingAccountsTradingControlsInterface,
        account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel = null,
        relatedCashAccount: TradingAccountModel = null,
        orderRouteService: OrderRouteService,
        tradingGroups: TradingGroupModel[] = [],
        currencies: CurrencyModel[] = [],
        fundPortalService?: FundPortalService,
        private client?: IndividualExtendedModel | CompanyExtendedModel,
        private omsRiskParameters?: OmsRiskParametersModel[],
    ) {
        this.tradingService = tradingService;
        this.controls = controls;
        this.relatedCashAccount = relatedCashAccount;
        this.account = account;
        this.tradingGroups = tradingGroups;
        this.fundPortalService = fundPortalService;
        this.currencies = currencies;
        this.formCreationErrors = [];
        this.routeFormControlsRouteArraySelector = TradingAccountsExecutionRoutesSelector;
    }

    private reInitGroupsControl(portalId: number): void {
        const isProPortal = portalId === PortalsEnum.AlaricPro;

        const reducedGroups = this.tradingGroups
            .filter((group: TradingGroupModel) => {
                if (!isProPortal) {
                    if (portalId === PortalsEnum.AlaricFundPortal) {
                        return group.accountIdentifier === AlaricFundPortalAccountIdentifier;
                    }
                    if (portalId === PortalsEnum.HammerWeb) {
                        return group.accountIdentifier === HammerWebPortalAccountIdentifier;
                    }
                    if (portalId === PortalsEnum.DemoAlaricPro) {
                        return group.accountIdentifier === DemoAlaricProPortalAccountIdentifier;
                    }

                    return group.accountIdentifier === TenEnPortalAccountIdentifier;
                }

                return [AlaricFundPortalAccountIdentifier, TenEnPortalAccountIdentifier, HammerWebPortalAccountIdentifier, DemoAlaricProPortalAccountIdentifier].indexOf(group.accountIdentifier) < 0;
            })
            .reduce((groups: groupedTradingGroupsOptions, group: TradingGroupModel) => {
                const option = {
                    label: !group.name ? group.groupReferenceId : group.name,
                    value: group.id
                };

                groups.options.push(option);
                groups.ids.push(group.id);

                return groups;
            }, {options: [], ids: []});

        const groupsControlValidators = [Validators.required, InArrayValidator(reducedGroups.ids)];

        this.getControlsForCashAccount().groups.options = reducedGroups.options;

        this.getControlsForCashAccount().groups.resetValue();
        this.getControlsForCashAccount().groups.resetValidators();
        this.getControlsForCashAccount().groups.setValidators(groupsControlValidators);

        this.form.get(TradingAccountsCashGroupSelector).reset();
        this.form.get(TradingAccountsCashGroupSelector).clearValidators();
        this.form.get(TradingAccountsCashGroupSelector).setValidators(groupsControlValidators);

        this.form.updateValueAndValidity();
    }

    private reInitSubTypesControl(portalId: number): void {
        let option: SelectOptionInterface = null;

        if (portalId === PortalsEnum.AlaricFundPortal) {
            option = {
                label: Helper.textsHelper().getAccountSubTypeName(AccountSubTypeEnum.FundCash),
                value: AccountSubTypeEnum.FundCash,
            };
        } else {
            option = {
                label: Helper.textsHelper().getAccountSubTypeName(AccountSubTypeEnum.TradingCash),
                value: AccountSubTypeEnum.TradingCash,
            };
        }

        this.getControlsForCashAccount().accountSubType.resetValue();
        this.getControlsForCashAccount().accountSubType.options = [option];

        this.form.get(TradingAccountsAccountSubType).reset();

        this.form.updateValueAndValidity();
    }

    private reInitCurrencyControl(portalId: number): void {
        let options: SelectOptionInterface[] = [];
        const isFundPortal = portalId === PortalsEnum.AlaricFundPortal;
        const control = this.getControlsForCashAccount().currency;
        const formControl = this.form.get(TradingAccountsCashCurrencySelector);

        control.resetValue();
        formControl.reset();

        if (!isFundPortal) {
            options = this.currencies
                .filter((c: CurrencyModel) => c.isTradeAvailable)
                .sort(
                    (n1, n2) =>
                        n1.currencyCode.localeCompare(n2.currencyCode)
                )
                .sort((n1, n2) => Number(n2.isDefaultForTrade) - Number(n1.isDefaultForTrade))
                .map((c: CurrencyModel): SelectOptionInterface => {
                    return {
                        label: c.currencyCode,
                        value: c.currencyCode,
                    };
                });
        } else {
            options = [
                {
                    label: Helper.textsHelper().getCurrencyTypesName(
                        CurrencyTypesEnum.Euro
                    ),
                    value: CurrencyTypesEnum.Euro,
                },
                {
                    label: Helper.textsHelper().getCurrencyTypesName(
                        CurrencyTypesEnum.Usd
                    ),
                    value: CurrencyTypesEnum.Usd,
                },
            ];
        }

        control.options = options;
        control.disabled = options.length === 1;

        if (options.length > 0) {
            control.value = options[0].value;
            formControl.patchValue(options[0].value);
        }

        this.form.updateValueAndValidity();
    }

    private validateFundAccountCreation(
        clientType: number,
        clientId: number,
        createRequestForCashAccountCreation: boolean
    ): boolean {
        const filteredGroups = this.tradingGroups.find((group: TradingGroupModel) => {
            return (
                (group.accountIdentifier === AlaricFundPortalAccountIdentifier) &&
                (group.status === TradingGroupStatusesEnum.Active)
            );
        });

        if (filteredGroups) {
            const filteredAccounts = filteredGroups.accounts.filter((account: TradingAccountModel) => {
                let ownerCheck = false;

                const subTypeValue = this.controls.accountSubType.value;

                const statusCheck = [
                    TradingAccountStatusesEnum.Closed,
                    TradingAccountStatusesEnum.Rejected
                ].indexOf(account.status) < 0;

                if (clientType === ClientTypesEnum.Individual) {
                    ownerCheck = account.ownerIndividualId === clientId;
                } else {
                    ownerCheck = account.ownerCompanyId === clientId;
                }

                const subTypeCheck = account.subType === subTypeValue;

                return (
                    (account.isCashAccount === createRequestForCashAccountCreation) &&
                    ownerCheck &&
                    subTypeCheck &&
                    statusCheck
                );
            });

            if (filteredAccounts.length > 0) {
                const message = createRequestForCashAccountCreation
                    ? PanelAccountsAddCashMessages.SameAccountSubType.message
                    : PanelAccountsAddTradingMessages.SameAccountSubType.message;

                this.formCreationErrors.push(message);

                return false;
            }
        }

        return true;
    }

    private isFundPortalRequestCheck(createRequestForCashAccount: boolean): boolean {
        if (!createRequestForCashAccount) {
            return (
                this.relatedCashAccount &&
                (this.relatedCashAccount.subType === AccountSubTypeEnum.FundCash)
            );
        }

        return this.getControlsForCashAccount().portals.value === PortalsEnum.AlaricFundPortal;
    }

    private createPutModelForTradingAccount(): TradingAccountPutModel {
        const tradingPlatform = !(this.getControlsForTradingAccount().tradingPlatforms) ? this.accountTradingPlatformId :
            this.getControlsForTradingAccount().tradingPlatforms.value;
        const vlrPercent = Helper.getAmountValue(this.getControlsForTradingAccount().vlrPercent.value);

        return new TradingAccountPutModel(
            this.accountId,
            false,
            this.controls.alias.value as string,
            this.controls.displayName.value as string,
            this.getControlsForTradingAccount().clearingAgent.value,
            this.getControlsForTradingAccount().clearingAccountRefId.value as string,
            tradingPlatform,
            this.account.accountReferenceId,
            this.getControlsForTradingAccount().currency.value as string,
            this.getControlsForTradingAccount().broker.value as number,
            null,
            null,
            vlrPercent,
        );
    }

    private createPutModelForCashAccount(): TradingAccountPutModel {
        return new TradingAccountPutModel(
            this.accountId,
            true,
            this.controls.alias.value as string,
            this.controls.displayName.value as string,
            null,
            null,
            null,
            this.account.accountReferenceId,
            this.getControlsForCashAccount().currency.value as string
        );
    }

    private updateAccount(
        createRequestForCashAccount: boolean,
        putModel: TradingAccountPutModel
    ): ServerResponse<number> {
        if (!this.form.dirty) {
            return Helper.createSuccessResponseObservable<number>(putModel.id);
        }

        return this.tradingService.updateTradingAccount(putModel).pipe(
            map((response: ServerResponseData<null>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    let state = null;

                    if (!createRequestForCashAccount) {
                        state = TradingAccountsFormErrorStatesEnum.UpdateCashAccountFailed;
                    } else {
                        state = TradingAccountsFormErrorStatesEnum.UpdateTradingAccountFailed;
                    }

                    return Helper.errorsHelper().createErrorResponse<null>(response, state);
                }

                return Helper.createSuccessResponse<number>(putModel.id);
            })
        );
    }

    private getOmsFilterContext(): { tradingPlatformId: number; currency: string } {
        return {
            tradingPlatformId: this.form.controls[TradingAccountsTradingPlatformSelector].value,
            currency: this.form.controls[TradingAccountsTradingCurrencySelector].value
        };
    }

    private filterOmsParametersByClearingAgent(clearingAgent: string): OmsRiskParametersModel[] {
        const { tradingPlatformId, currency } = this.getOmsFilterContext();

        return this.omsRiskParameters.filter(p =>
            p.clearing === clearingAgent &&
            p.tradingPlatformId === tradingPlatformId &&
            p.currency === currency);
    }

    private filterOmsParametersByFirm(clearingAgent: string, firmId: string): OmsRiskParametersModel[] {
        const { tradingPlatformId, currency } = this.getOmsFilterContext();

        return this.omsRiskParameters.filter(p =>
            p.clearing === clearingAgent &&
            p.firmId === firmId &&
            p.tradingPlatformId === tradingPlatformId &&
            p.currency === currency);
    }

    private filterOmsParametersByGroup(clearingAgent: string, firmId: string, groupId: string): OmsRiskParametersModel[] {
        const { tradingPlatformId, currency } = this.getOmsFilterContext();

        return this.omsRiskParameters.filter(p =>
            p.clearing === clearingAgent &&
            p.firmId === firmId &&
            p.groupId === groupId &&
            p.tradingPlatformId === tradingPlatformId &&
            p.currency === currency);
    }

    private findBestOmsSettings(omsParameters: OmsRiskParametersModel[]): OmsRiskParametersModel {
        if (this.client) {

            const dailyBp = this.client.dailyBuyingPowerMultiple ?? 1;
            const matchByDailyBp = omsParameters
                .find(p => p.limits?.allAssetClasses?.totalDynExpMult?.limit === dailyBp);

            return matchByDailyBp || omsParameters[0];
        }

        return omsParameters[0];
    }

    public onClearingAgentChange(clearingAgent: string): OmsRiskParametersModel {
        if (!this.omsRiskParameters) {
            return null;
        }

        if (!clearingAgent) {
            this.alertsService.addAlert('Please select clearing agent.');
            return null;
        }

        const foundOmsParameters = this.filterOmsParametersByClearingAgent(clearingAgent);
        this.initOmsFirmOptions(foundOmsParameters);

        let foundOmsSettings: OmsRiskParametersModel = null;

        if (foundOmsParameters.length > 0) {
            foundOmsSettings = this.findBestOmsSettings(foundOmsParameters);

            if (foundOmsSettings) {
                this.form.controls[TradingAccountsTradingClearingAccountRefIdSelector].setValue(foundOmsSettings.clearingAccountId);
                this.form.controls[TradingAccountsTradingAlias].setValue(foundOmsSettings.alias);
                this.form.controls[TradingAccountsDisabledSelector].setValue(foundOmsSettings.disabled);
                this.omsSkipFundingDefault = foundOmsSettings.skipAwaitingFunding;
                this.applySkipFundingDefaultForCount(this.getNumberOfAccounts());
                this.form.controls[TradingAccountsFirmIdSelector].setValue(foundOmsSettings.firmId);
            }
        }
        else {
            this.initOmsGroupOptions([]);
        }

        return foundOmsSettings;
    }

    /**
     * The OMS firm / group options are filtered by trading platform and currency, so they go
     * stale when the platform changes after a clearing agent has been picked. Rebuild them and
     * re-enter the firm -> group chain; the clearing account id and alias the operator already
     * typed are deliberately left alone.
     */
    public onTradingPlatformChange(): void {
        if (!this.omsRiskParameters) {
            return;
        }

        const clearingAgent = this.form.controls[TradingAccountsTradingClearingAgentSelector].value;

        if (!clearingAgent) {
            return;
        }

        const foundOmsParameters = this.filterOmsParametersByClearingAgent(clearingAgent);
        this.initOmsFirmOptions(foundOmsParameters);

        if (foundOmsParameters.length === 0) {
            this.initOmsGroupOptions([]);

            return;
        }

        const foundOmsSettings = this.findBestOmsSettings(foundOmsParameters);

        if (foundOmsSettings) {
            this.form.controls[TradingAccountsFirmIdSelector].setValue(foundOmsSettings.firmId);
        }
    }

    public onFirmIdChange(firmId: string): OmsRiskParametersModel {
        if (!this.omsRiskParameters) {
            return null;
        }

        const clearingAgent = this.form.controls[TradingAccountsTradingClearingAgentSelector].value;
        if (!clearingAgent) {
            return null;
        }

        const foundOmsParameters = this.filterOmsParametersByFirm(clearingAgent, firmId);
        this.initOmsGroupOptions(foundOmsParameters);
        let foundOmsSettings: OmsRiskParametersModel = null;

        if (foundOmsParameters.length > 0) {
            foundOmsSettings = this.findBestOmsSettings(foundOmsParameters);

            if (foundOmsSettings) {
                this.form.controls[TradingAccountsGroupIdSelector].setValue(foundOmsSettings.groupId);
            }
        }

        return foundOmsSettings;
    }

    public onGroupIdChange(groupId: string): OmsRiskParametersModel {
        if (!this.omsRiskParameters) {
            return null;
        }

        const clearingAgent = this.form.controls[TradingAccountsTradingClearingAgentSelector].value;
        const firmId = this.form.controls[TradingAccountsFirmIdSelector].value;

        if (!clearingAgent || !firmId) {
            return null;
        }

        const foundOmsParameters = this.filterOmsParametersByGroup(clearingAgent, firmId, groupId);
        let foundOmsSettings: OmsRiskParametersModel = null;

        if (foundOmsParameters.length > 0) {
            foundOmsSettings = this.findBestOmsSettings(foundOmsParameters);

            if (foundOmsSettings) {
                const limits = foundOmsSettings.limits;

                if (limits) {
                    this.applyTemplateValue(TradingAccountsTotalDynExpLimitSelector, limits.allAssetClasses?.totalDynExpMult?.limit);
                    this.applyTemplateValue(TradingAccountsTotalDynExpMultSelector, limits.allAssetClasses?.totalDynExpMult?.mult);

                    this.applyTemplateValue(TradingAccountsLossLimitSelector, limits.allAssetClasses?.lossLimitPct?.limit);
                    this.applyTemplateValue(TradingAccountsPosLimitSelector, limits.allAssetClasses?.posLimitPct?.limit);

                    this.applyTemplateValue(TradingAccountsMaxOrderQtySelector, limits.equities?.maxOrderQty?.limit);

                    this.reInitOmsRiskParametersAction(TradingAccountsTotalDynExpActionSelector, limits.allAssetClasses?.totalDynExpMult?.action);
                    this.reInitOmsRiskParametersAction(TradingAccountsLossLimitActionSelector, limits.allAssetClasses?.lossLimitPct?.action);
                    this.reInitOmsRiskParametersAction(TradingAccountsPosLimitActionSelector, limits.allAssetClasses?.posLimitPct?.action);
                    this.reInitOmsRiskParametersAction(TradingAccountsShortSaleActionSelector, limits.equities?.shortSale?.action);
                    this.reInitOmsRiskParametersAction(TradingAccountsMaxOrderQtyActionSelector, limits.equities?.maxOrderQty?.action);
                }
            }
        }

        return foundOmsSettings;
    }

    private initOmsFirmOptions(omsRiskParameterModels: OmsRiskParametersModel[]): void {
        const controls = this.getControlsForTradingAccount();
        const firmControl = controls.firmId;
        firmControl.options.splice(0, firmControl.options.length);

        if (omsRiskParameterModels.length == 0) {
            firmControl.options.push({label: 'No firms found', value: '', disabled: true});
            return;
        }

        const onlyUnique = (item, index, array) => array.map(x => x.value).indexOf(item.value) === index;

        const firmOptions = omsRiskParameterModels.map(o => {
            return {
                label: o.firmId,
                value: o.firmId
            }
        }).filter(onlyUnique);
        firmControl.options.push(...firmOptions);
    }

    private initOmsGroupOptions(omsRiskParameterModels: OmsRiskParametersModel[]) {
        const controls = this.getControlsForTradingAccount();
        const groupControl = controls.groupId;
        groupControl.options.splice(0, groupControl.options.length);

        if (omsRiskParameterModels.length == 0) {
            groupControl.options.push({label: 'No groups found', value: '', disabled: true});
            return;
        }

        const onlyUnique = (item, index, array) => array.map(x => x.value).indexOf(item.value) === index;

        const groupOptions = omsRiskParameterModels.map(o => {
            return {
                label: o.groupId,
                value: o.groupId
            }
        }).filter(onlyUnique);
        groupControl.options.push(...groupOptions);
    }

    /**
     * Applies an OMS template value, but never over something the operator typed.
     *
     * Angular only marks a control dirty on a view-to-model update, so `dirty` is an exact
     * "the operator touched this" signal here. emitViewToModelChange keeps the cascade single
     * pass - without it setValue() re-fires (ngModelChange) and the chain re-enters itself.
     */
    private applyTemplateValue(controlSelector: string, value: any): void {
        const formControl = this.form.controls[controlSelector];

        if (!formControl || formControl.dirty) {
            return;
        }

        formControl.setValue(value, {emitViewToModelChange: false});
    }

    private reInitOmsRiskParametersAction(controlSelector: string, riskAction: OmsRiskActionModel): void {
        const formControl = this.form.controls[controlSelector];

        if (!riskAction || !formControl || formControl.dirty) {
            return;
        }

        const control = this.getControlsForTradingAccount()[controlSelector] as SelectControl;
        control.value = [];

        if (riskAction.doNothing) {
            control.value.push(SelectorActionDoNothing);
        }
        if (riskAction.popupWarning) {
            control.value.push(SelectorActionPopupWarning);
        }
        if (riskAction.rejectOrder) {
            control.value.push(SelectorActionRejectOrder);
        }
        if (riskAction.sendEmail) {
            control.value.push(SelectorActionSendEmail);
        }
        if (riskAction.sendSms) {
            control.value.push(SelectorActionSendSms);
        }
        if (riskAction.setToBlock) {
            control.value.push(SelectorActionSetToBlock);
        }
        if (riskAction.setToCloseout) {
            control.value.push(SelectorActionSetToCloseout);
        }

        formControl.setValue(control.value, {emitViewToModelChange: false});
    }

    private getMarginRiskActionInterface(controlName: string): OmsRiskAction {
        const control = this.form.controls[controlName];
        const controlValue = [].concat(control?.value) || [];

        const isSelected = (key: string): boolean => {
            return controlValue.indexOf(key) >= 0;
        }

        return {
            do_nothing: isSelected(SelectorActionDoNothing),
            reject_order: isSelected(SelectorActionRejectOrder),
            popup_warning: isSelected(SelectorActionPopupWarning),
            send_email: isSelected(SelectorActionSendEmail),
            send_sms: isSelected(SelectorActionSendSms),
            set_to_closeout: isSelected(SelectorActionSetToCloseout),
            set_to_block: isSelected(SelectorActionSetToBlock),
        };
    }

    /**
     * Reads every risk limit straight off the FormGroup.
     *
     * The descriptor controls are only a mirror of the form and cannot represent a cleared field
     * (BaseControl.value is never assigned a null), so reading them silently resurrects whatever
     * the clearing agent / firm / group cascade last auto-filled. The form is the single source
     * of truth - see getMarginRiskActionInterface() and getOmsFilterContext(), which already use it.
     */
    private buildRiskLimitsPayload(): OmsRiskParametersModel {
        const raw = this.form.getRawValue();

        // Amount inputs hold thousand separated strings ('10,000'); getAmountValue is separator aware.
        const amount = (selector: string, fallback: number = null): number => {
            const parsed = Helper.getAmountValue(raw[selector]);

            return ((parsed === null) || (parsed === undefined) || isNaN(parsed)) ? fallback : parsed;
        };

        return new OmsRiskParametersModel({
            trading_platform_id: raw[TradingAccountsTradingPlatformSelector],
            clearing_account_id: raw[TradingAccountsTradingClearingAccountRefIdSelector]?.toString(),
            alias: raw[TradingAccountsTradingAlias]?.toString(),
            clearing: raw[TradingAccountsTradingClearingAgentSelector],
            firm_id: raw[TradingAccountsFirmIdSelector],
            group_id: raw[TradingAccountsGroupIdSelector],
            currency: raw[TradingAccountsTradingCurrencySelector],
            disabled: raw[TradingAccountsDisabledSelector] as boolean,
            skip_awaiting_funding: raw[TradingAccountsSkipFundingSelector] as boolean,
            group_name: '',
            trading_platform_name: '',
            limits: {
                all_asset_classes: {
                    total_dyn_exp_mult: {
                        // Limit and mult are non nullable on the API and get clamped to 1 there anyway.
                        limit: amount(TradingAccountsTotalDynExpLimitSelector, 1),
                        mult: amount(TradingAccountsTotalDynExpMultSelector, 1),
                        action: this.getMarginRiskActionInterface(TradingAccountsTotalDynExpActionSelector),
                    },
                    loss_limit_pct: {
                        limit: amount(TradingAccountsLossLimitSelector),
                        action: this.getMarginRiskActionInterface(TradingAccountsLossLimitActionSelector),
                    },
                    pos_limit_pct: {
                        limit: amount(TradingAccountsPosLimitSelector),
                        action: this.getMarginRiskActionInterface(TradingAccountsPosLimitActionSelector),
                    }
                },
                equities: {
                    max_order_qty: {
                        limit: amount(TradingAccountsMaxOrderQtySelector),
                        action: this.getMarginRiskActionInterface(TradingAccountsMaxOrderQtyActionSelector),
                    },
                    short_sale: {
                        action: this.getMarginRiskActionInterface(TradingAccountsShortSaleActionSelector),
                    }
                }
            }
        });
    }

    private createModelForTradingAccount(clientType: number, clientId: number): TradingAccountInputModel {
        const isIndividualClient = (clientType === ClientTypesEnum.Individual);
        const routeIds = this.form.get(TradingAccountsExecutionRoutesSelector).value
            .filter((id: number) => (id !== null));
        const routeConfig = new RouteConfigInputModel(
            this.form.value[this.getControlsForTradingAccount().broker.name] as number,
            routeIds,
            this.form.value[this.getControlsForTradingAccount().routeSets.name] as number[]
        );

        // Read the form, not the descriptor mirror, so this stays in step with buildRiskLimitsPayload().
        const dailyBp = Helper.getAmountValue(this.form.controls[TradingAccountsTotalDynExpLimitSelector].value);
        const overnightBp = dailyBp > 1 ? dailyBp / 3 : 1;
        const omsPayload = this.buildRiskLimitsPayload();

        const model = new TradingAccountInputModel(
            false,
            !isIndividualClient,
            !isIndividualClient ? null : clientId,
            !isIndividualClient ? clientId : null,
            this.getControlsForTradingAccount().currency.value as string,
            this.getControlsForTradingAccount().alias.value as string,
            this.getControlsForTradingAccount().displayName.value as string,
            this.relatedCashAccount.accountGroupId,
            this.getControlsForTradingAccount().clearingAgent.value,
            this.getControlsForTradingAccount().clearingAccountRefId.value as string,
            null,
            null,
            this.getControlsForTradingAccount().tradingPlatforms.value as number,
            this.relatedCashAccount.portalUserType,
            null,
            this.relatedCashAccount.id,
            this.getControlsForTradingAccount().accountSubType.value,
            routeConfig,
            dailyBp > 1,
            dailyBp,
            overnightBp,
            omsPayload
        );

        return model;
    }

    // --- Batch trading-account creation ---

    getNumberOfAccounts(): number {
        const control = this.form.get(TradingAccountsNumberOfAccountsSelector);

        if (!control) {
            return 1;
        }

        const parsed = Math.round(Helper.getAmountValue(control.value));

        return parsed > 0 ? parsed : 1;
    }

    markSkipFundingOperatorTouched(): void {
        if (this.suppressSkipFundingTouched) {
            return;
        }

        this.skipFundingOperatorTouched = true;
    }

    applySkipFundingDefaultForCount(count: number): void {
        const skipFundingControl = this.form.get(TradingAccountsSkipFundingSelector);
        const skipFundingDescriptor = this.getControlsForTradingAccount().skipFunding;

        if (!skipFundingControl || !skipFundingDescriptor) {
            return;
        }

        let shouldSkip: boolean;

        if (count > 1) {
            if (this.skipFundingOperatorTouched) {
                return;
            }

            shouldSkip = true;
        } else {
            shouldSkip = this.omsSkipFundingDefault;
            this.skipFundingOperatorTouched = false;
        }

        this.suppressSkipFundingTouched = true;
        try {
            skipFundingDescriptor.value = shouldSkip;
            skipFundingDescriptor.setChecked(shouldSkip);
            skipFundingControl.patchValue(shouldSkip, {emitEvent: false});
        } finally {
            this.suppressSkipFundingTouched = false;
        }
    }

    submitAccountBatch(clientType: number, clientId: number, count: number): ServerResponse<BatchCreateAccountResultInterface> {
        const characteristics = this.createModelForTradingAccount(clientType, clientId);

        return this.tradingService.registerAccountBatch(new BatchCreateAccountInputModel(characteristics, count));
    }

    private createModelForFundTradingAccount(clientId: number): FundPortalTradingAccountInputModel {
        const filteredGroups = this.tradingGroups.find((group: TradingGroupModel) => {
            return group.id === this.relatedCashAccount.accountGroupId;
        });

        return new FundPortalTradingAccountInputModel(
            clientId,
            this.getControlsForTradingAccount().accountSubType.value,
            this.relatedCashAccount.id,
            null,
            this.controls.displayName.value as string,
            filteredGroups.accountIdentifier ?? null,
            this.controls.currency.value
        );
    }

    private createModelForCashAccount(clientType: number, clientId: number): TradingAccountInputModel {
        const isIndividualClient = (clientType === ClientTypesEnum.Individual);

        const userBehaviourType = (this.getControlsForCashAccount().portals.value as number === PortalsEnum.AlaricPro)
            ? UserBehaviourTypesEnum.Pro : UserBehaviourTypesEnum.Retail;

        return new TradingAccountInputModel(
            true,
            !isIndividualClient,
            !isIndividualClient ? null : clientId,
            !isIndividualClient ? clientId : null,
            this.getControlsForCashAccount().currency.value as string,
            this.getControlsForCashAccount().alias.value as string,
            this.getControlsForCashAccount().displayName.value as string,
            this.getControlsForCashAccount().groups.value as number,
            null,
            null,
            null,
            null,
            null,
            userBehaviourType,
            this.getControlsForCashAccount().portals.value as number,
            null,
            this.getControlsForCashAccount().accountSubType.value as number
        );
    }

    private createModelForFundCashAccount(clientId: number): FundPortalCashAccountInputModel {
        const groupsControlValue = this.getControlsForCashAccount().groups.value as number;

        const filteredGroups = this.tradingGroups.find((group: TradingGroupModel) => {
            return group.id === groupsControlValue;
        });

        return new FundPortalCashAccountInputModel(
            clientId,
            this.controls.currency.value,
            filteredGroups.accountIdentifier ?? null,
            null,
            this.getControlsForCashAccount().displayName.value as string
        );
    }

    private registerAccountMap(
        createRequestForCashAccount: boolean,
        response: ServerResponseData<TradingAccountInterface>
    ): ServerResponseData<number> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            let state = null;

            if (!createRequestForCashAccount) {
                state = TradingAccountsFormErrorStatesEnum.CreateCashAccountFailed;
            } else {
                state = TradingAccountsFormErrorStatesEnum.CreateTradingAccountFailed;
            }

            return Helper.errorsHelper().createErrorResponse<TradingAccountInterface>(response, state);
        }

        const data = response.data as TradingAccountInterface;
        const model = new TradingAccountModel(data);

        return Helper.createSuccessResponse<number>(model.id);
    }

    public reInitRouteSets(routeSets: RouteSetSearchOutputModel, selectedRouteSetIds: number[] = []): void {
        const control = this.getControlsForTradingAccount().routeSets;
        const formControl = this.form.get(control.name);

        control.resetValue();
        formControl.reset();
        control.options = routeSets.content.map(item =>
            ({value: item.getId(), label: item.getName()} as SelectOptionInterface));
        control.value = selectedRouteSetIds;
        formControl.patchValue(selectedRouteSetIds);

        this.form.updateValueAndValidity();
    }

    public reInitRoutes(routes: RouteSearchOutputModel, selectedRouteIds: number[] = []): void {
        const routeFormArray = this.form.get(TradingAccountsExecutionRoutesSelector) as FormArray;
        routeFormArray.clear();
        this.getControlsForTradingAccount().routes = routes.content.map((r: RouteSearchOutputItemModel, index: number) => {
            const isSelected = selectedRouteIds.indexOf(r.getId()) > -1;

            return new CheckboxControl({
                name: index + '',
                value: r.getId(),
                placeholder: r.getDisplayName(),
                validators: [],
                componentType: ComponentTypesEnum.Checkbox,
                checked: isSelected,
                color: ColorCodesEnum.Primary,
                expectBooleanValue: false
            });
        });
        this.getControlsForTradingAccount().routes.forEach((checkbox: CheckboxControl) => {
            const checkboxFormControl = new FormControl(checkbox.value, checkbox.validators);
            routeFormArray.push(checkboxFormControl);
        });

        this.form.updateValueAndValidity();
    }

    getControlsForCashAccount(): TradingAccountsCashControlsInterface {
        return this.controls as TradingAccountsCashControlsInterface;
    }

    getControlsForTradingAccount(): TradingAccountsTradingControlsInterface {
        return this.controls as TradingAccountsTradingControlsInterface;
    }

    onPortalChange(portalId: number): void {
        this.reInitGroupsControl(portalId);
        this.reInitSubTypesControl(portalId);
        this.reInitCurrencyControl(portalId);
    }

    initForm(accountId: number = null, accountTradingPlatformId: number = null): void {
        const routeIdsFormArray = new FormArray([]);
        this.getControlsForTradingAccount().routes?.forEach((checkbox: CheckboxControl) => {
            const checkboxFormControl = new FormControl(checkbox.value, checkbox.validators);
            routeIdsFormArray.push(checkboxFormControl);
        });
        this.form = new FormGroup({});
        this.form.addControl(TradingAccountsExecutionRoutesSelector, routeIdsFormArray);

        this.accountId = accountId;
        this.accountTradingPlatformId = accountTradingPlatformId;
        const nonIterableControlNames = [
            TradingAccountsExecutionRoutesSelector
        ];
        Object.keys(this.controls).forEach((key: string) => {
            const control = this.controls[key];

            if (this.account && (key === TradingAccountsAccountSubType)) {
                control.value = this.account.AccountSubType;
            }

            if (nonIterableControlNames.indexOf(control.name) <= -1) {
                this.form.addControl(control.name, new FormControl(control.value, control.validators));
            }
        });
    }

    validateForm(
        messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; },
        clientType: number = null,
        clientId: number = null,
        createRequestForTradingAccountCreation = false,
        createRequestForCashAccountCreation = false,
    ): boolean {
        if (this.form.invalid) {
            Object.keys(this.controls).forEach((key: string) => {
                const control = this.controls[key];
                const formControl = this.form.get(control.name);

                if (formControl && formControl.invalid) {
                    control.errors = Object.keys(formControl.errors)
                        .map((errorKey: string) => {
                            if (errorKey === 'minlength' || errorKey === 'maxlength') {
                                return messageFunction(control.placeholder, formControl.errors[errorKey].requiredLength)[errorKey];
                            } else if (errorKey === AmountMinValidatorKey) {
                                return messageFunction(control.placeholder, formControl.errors[errorKey].min)[errorKey];
                            } else if (errorKey === AmountMaxValidatorKey) {
                                return messageFunction(control.placeholder, formControl.errors[errorKey].max)[errorKey];
                            }

                            return messageFunction(control.placeholder)[errorKey];

                        });
                }
            });

            return false;
        }

        if (createRequestForTradingAccountCreation || createRequestForCashAccountCreation) {
            if (this.isFundPortalRequestCheck(createRequestForCashAccountCreation)) {
                return this.validateFundAccountCreation(clientType, clientId, createRequestForCashAccountCreation);
            }
        }

        return true;
    }

    createRequest(
        createRequestForAccountCreation: boolean,
        createRequestForCashAccount: boolean,
        clientType: number = null,
        clientId: number = null,
    ): ServerResponse<number> {
        if (!createRequestForAccountCreation) {
            let putModel: TradingAccountPutModel = null;

            if (!createRequestForCashAccount) {
                putModel = this.createPutModelForTradingAccount();
            } else {
                putModel = this.createPutModelForCashAccount();
            }

            return this.updateAccount(createRequestForCashAccount, putModel);
        }

        const isFundPortalRequest = this.isFundPortalRequestCheck(createRequestForCashAccount);

        let inputModel: TradingAccountInputModel | FundPortalTradingAccountInputModel | FundPortalCashAccountInputModel = null;

        if (!isFundPortalRequest) {
            inputModel = !createRequestForCashAccount
                ? this.createModelForTradingAccount(clientType, clientId)
                : this.createModelForCashAccount(clientType, clientId);

            return this.tradingService.registerAccount(inputModel as TradingAccountInputModel).pipe(
                map(this.registerAccountMap.bind(this, createRequestForCashAccount))
            );
        }

        if (!createRequestForCashAccount) {
            inputModel = this.createModelForFundTradingAccount(clientId);

            return this.fundPortalService.registerTradingAccount(inputModel as FundPortalTradingAccountInputModel).pipe(
                map(this.registerAccountMap.bind(this, false))
            );
        }

        inputModel = this.createModelForFundCashAccount(clientId);

        return this.fundPortalService.registerCashAccount(inputModel as FundPortalCashAccountInputModel).pipe(
            map(this.registerAccountMap.bind(this, true))
        );
    }
}

export default TradingAccountsForm
